'use client';

import { useState, useCallback, useEffect, useRef, useLayoutEffect, type FC, type CSSProperties } from 'react';
import { cn } from '../../utils/cn';
import { getGhostSrc } from '../../utils/ghostSkeleton';
import { forgetImageLoaded, isImageLoaded, markImageLoaded } from './loadedImages';

// WHY: два кадра: кэшированная картинка приходит следующим тиком; с отсрочкой 120 мс пустота держалась семь кадров
const CACHE_GRACE_MS = 32;

interface SkinImageProps {
  name: string;
  src?: string | null;
  alt?: string;
  imgClassName?: string;
  imgStyle?: CSSProperties;
  className?: string;
  style?: CSSProperties;
  priority?: boolean;
}

export const SkinImage: FC<SkinImageProps> = ({
  name, src, alt = name, imgClassName, imgStyle, className, style, priority = false,
}) => {
  const ghost = getGhostSrc(name);
  const [isLoaded, setIsLoaded] = useState(() => isImageLoaded(src));
  const [hasFailed, setHasFailed] = useState(false);
  const [instant, setInstant] = useState(() => isImageLoaded(src));
  const prevSrcRef = useRef(src);
  const nodeRef = useRef<HTMLImageElement | null>(null);

  useLayoutEffect(() => {
    if (prevSrcRef.current !== src) {
      prevSrcRef.current = src;
      const known = isImageLoaded(src);
      setIsLoaded(known);
      setInstant(known);
      setHasFailed(false);
    }
  }, [src]);

  // WHY: картинка из кэша приходит в ref уже `complete`, `onLoad` по ней не сработает — состояние снимаем с элемента
  const captureNode = useCallback(
    (node: HTMLImageElement | null) => {
      nodeRef.current = node;
      if (!node) return;
      if (node.complete && node.naturalWidth > 0) {
        markImageLoaded(src);
        setIsLoaded(true);
      }
    // WHY: `complete === false` на этом кадре бывает и у кэшированной картинки — возврат силуэта по нему вернёт мигание
    },
    [src],
  );

  useEffect(() => {
    if (!src || !instant || !isLoaded) return;
    const node = nodeRef.current;
    if (node?.complete && node.naturalWidth > 0) return;

    const timer = window.setTimeout(() => {
      const current = nodeRef.current;
      if (current && current.complete && current.naturalWidth > 0) return;
      forgetImageLoaded(src);
      setIsLoaded(false);
      setInstant(false);
    }, CACHE_GRACE_MS);

    return () => window.clearTimeout(timer);
  }, [src, instant, isLoaded]);

  const handleLoad = useCallback(() => {
    markImageLoaded(src);
    setIsLoaded(true);
  }, [src]);

  const handleError = useCallback(() => {
    forgetImageLoaded(src);
    setHasFailed(true);
  }, [src]);

  const showGhost = !src || !isLoaded || hasFailed;

  return (
    <div className={cn('relative w-full h-full', className)} style={style}>
      {showGhost && (
        <img
          src={ghost}
          alt=""
          aria-hidden
          decoding="async"
          className={cn('absolute inset-0 opacity-50 pointer-events-none', imgClassName)}
        />
      )}
      {src && !hasFailed && (
        <img
          ref={captureNode}
          src={src}
          alt={alt}
          loading={priority || instant ? 'eager' : 'lazy'}
          decoding={instant ? 'sync' : 'async'}
          fetchPriority={priority ? 'high' : 'auto'}
          onLoad={handleLoad}
          onError={handleError}
          style={imgStyle}
          className={cn(
            'relative',
            instant ? undefined : 'transition-opacity duration-200',
            isLoaded ? 'opacity-100' : 'opacity-0',
            imgClassName,
          )}
        />
      )}
    </div>
  );
};
